import * as React from "react";
import { useTranslation } from "react-i18next";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Button, Checkbox, FormControlLabel, Grid, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/system";

import AdsAddOn from "./AdsAddOn";
import "./lotterySystem.css";

const Pricing: React.FC = () => {
  const { t } = useTranslation("translation");
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [basicValue, setBasicValue] = React.useState<number>(0);
  const [basicPlusValue, setBasicPlusValue] = React.useState<number>(0);
  const [proValue, setProValue] = React.useState<number>(0);
  const [basic5Check, setBasic5Check] = React.useState<boolean>(false);
  const [basic10Check, setBasic10Check] = React.useState<boolean>(false);
  const [basicPlusCheck, setBasicPlusCheck] = React.useState<boolean>(false);
  const [proCheck, setProCheck] = React.useState<boolean>(false);
  const [monthlyOrAnnualPlanCheck, setMonthlyOrAnnualPlanCheck] = React.useState<boolean>(false);

  const CustomSwitch = styled(Switch)(({ theme }) => ({
    width: 50,
    height: 24,
    padding: 0,
    display: "flex",
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(26px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#4caf50",
          opacity: 1,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: 20,
      height: 20,
      boxShadow: "none",
    },
    "& .MuiSwitch-track": {
      borderRadius: 12,
      backgroundColor: "#e0e0e0",
      opacity: 1,
    },
  }));

  const handleBasicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBasic10Check(e.target.checked);
    if (e.target.checked) {
      setBasicValue(1);
    } else {
      setBasicValue(0);
    }
    handleBasicReset();
  };

  const handleBasicReset = () => {
    setBasicPlusValue(0);
    setProValue(0);
    setBasicPlusCheck(false);
    setProCheck(false);
  };

  const handleBasicPlusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBasicPlusCheck(e.target.checked);
    if (e.target.checked) {
      setBasicPlusValue(1);
    } else {
      setBasicPlusValue(0);
    }
    handleBasicPlusReset();
  };

  const handleBasicPlusReset = () => {
    setBasicValue(0);
    setProValue(0);
    setBasic10Check(false);
    setProCheck(false);
    setBasic5Check(false);
  };

  const handleProChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProCheck(e.target.checked);
    if (e.target.checked) {
      setProValue(1);
    } else {
      setProValue(0);
    }
    handleProReset();
  };

  const handleProReset = () => {
    setBasicValue(0);
    setBasicPlusValue(0);
    setBasic10Check(false);
    setBasicPlusCheck(false);
    setBasic5Check(false);
  };

  const handleBasicTotal = () => {
    let total: number = monthlyOrAnnualPlanCheck ? 240 : 25;
    if (basic10Check) total = total + 10 * basicValue;
    if (basic5Check) total = total + 5;
    return total;
  };

  const handleBasicPlusTotal = () => {
    let total: number = monthlyOrAnnualPlanCheck ? 384 : 40;
    if (basicPlusCheck) total = total + 10 * basicPlusValue;
    return total;
  };

  const handleProTotal = () => {
    let total: number = monthlyOrAnnualPlanCheck ? 480 : 50;
    if (proCheck) total = total + 10 * proValue;
    return total;
  };

  const basicPlusOrangeListingIndex = [2, 3, 4, 5];
  const popularOrangeListingIndex = [5];
  const premiumOrangeListingIndex = [6];

  const gotoScroll = (id: string) => {
    const target = document?.getElementById(id);
    const targetIds = ["lottery-display", "features4", "about-us", "contact-us"];
    const windowScrollIds = ["digital-signage", "pricing"];
    if (target && targetIds.includes(id)) {
      target.scrollIntoView({ behavior: "smooth" });
    } else if (windowScrollIds.includes(id)) {
      const offset = target?.offsetTop;
      const headerHeight = 110; // Replace with your actual header height
      if (offset) {
        window.scrollTo({
          top: offset - headerHeight,
          behavior: "smooth",
        });
      }
    } else {
      target?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Grid>
      <>
        <Typography
          className='landing-section-heading landing-secondary-color landing-headers-orange-color'
          variant='h6'
          align='center'
          id='pricing'
        >
          {t("pricing")}
        </Typography>
        <Typography className='landing-feature-description-typo' align='center'>
          {t("pricingDescription")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 2,
            backgroundColor: "#f8f8f8",
            border: "1px solid #ccc",
            borderRadius: "8px",
            fontFamily: "Arial, sans-serif",
            width: "100%",
            maxWidth: 600,
            margin: "0 auto",
          }}
        >
          {/* Billing Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant='body1'> Monthly Billing </Typography>
            <CustomSwitch
              checked={monthlyOrAnnualPlanCheck}
              onChange={(e) => setMonthlyOrAnnualPlanCheck(e.target.checked)}
            />
            <Typography variant='body1'> Annual Billing </Typography>
          </Box>

          {/* Discount Section */}
          <Box
            sx={{
              backgroundColor: "#ff5722",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "5px",
              textAlign: "center",
              mb: 2,
            }}
          >
            <Typography variant='h6' sx={{ fontWeight: "bold" }}>
              SAVE 20 %
            </Typography>
            <Typography variant='body2'> GET 20 % OFF for 5 + Store Signup </Typography>
          </Box>

          {/* Action Text */}
          <Grid container spacing={1} sx={{ textAlign: "center" }}>
            <Grid item xs={12}>
              <Typography variant='body1' sx={{ color: "#007bff", fontWeight: "bold" }}>
                Select Digi Tech Express for your business and boost your sale
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {/* <Typography variant="body2" sx = {{ color: "#4caf50" }}>
                Make any screen into a Digital Revolution
                    </Typography> */}
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} mt={5} direction={isMobileOrTablet ? 'column' : 'row'}>
          <Grid className={`landing-pricing-grid landing-pricing-grid-background ${isMobileOrTablet && 'landing-pricing-grid-width'}`} item xs={12} sm={6} md={3} style={{
            marginBottom: isMobileOrTablet ? "20px" : "0",
            marginLeft: isMobileOrTablet ? "20px" : "0",
          }}>
            <Button className='landing-pricing-pro-out-popular' variant='contained'>
              {t("hide")}
            </Button>
            <Typography className='landing-secondary-color landing-pricing-heading'>
              {t("basic")}
            </Typography>
            <Typography className='landing-secondary-color landing-price-title'>
              <sup className='landing-price-super-script'> $ </sup>
              {monthlyOrAnnualPlanCheck ? 240 : 25}
              <sub className='landing-price-sub-script'>
                {" "}
                /{monthlyOrAnnualPlanCheck ? "year" : "mo"}.
              </sub>
            </Typography>
            <Typography className='landing-price-description'> {t("singleDisplay")} </Typography>
            {Array(2)
              .fill("")
              .map((element: string, index: number) => (
                <Grid key={index} display='flex' mt={2}>
                  <CheckCircleIcon className='landing-pricing-content-list-icon landing-secondary-color' />
                  <Typography className='landing-text-color landing-list-item-align'>
                    {t(`basicList${index + 1}`)}
                  </Typography>
                </Grid>
              ))}
            <Grid display='flex' mt={2}>
              <CheckCircleIcon className='landing-pricing-content-list-icon landing-secondary-color' />
              <Typography className='landing-text-color landing-list-item-align'>
                {t("24/7Support")}
              </Typography>
            </Grid>
            {/* TODO: later - instead of margin top use flex property */}
            <Grid className='landing-pricing-extra-grid' mt='77%'>
              {/* <FormControlLabel
              className='landing-pricing-checkbox'
              control={
                <Checkbox
                  checked={basic5Check}
                  onChange={() => {
                    handleBasicReset();
                    setBasic5Check(!basic5Check);
                  }}
                />
              }
              label={t("+$5")}
            /> */}
              {/* <Typography className='landing-pricing-checkbox'>{t("$5Description")}</Typography> */}
              <Grid display='flex' alignContent='space-between'>
                {/* <FormControlLabel
                className='landing-pricing-checkbox'
                control={
                  <Checkbox
                    checked={basic10Check}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBasicChange(e)}
                  />
                }
                label={t("+$10")}
              /> */}
                {/* <AdsAddOn value={basicValue} setValue={setBasicValue} setCheck={setBasic10Check} handleReset={handleBasicReset}/> */}
              </Grid>
              {/* <Typography className='landing-pricing-checkbox'>{t("$10Description")}</Typography> */}
              <Typography className='landing-secondary-color landing-pricing-total' align='center'>
                {t("total") + `$${handleBasicTotal()}.00`}
              </Typography>
            </Grid>
            <Button
              className='landing-pricing-button-variant'
              variant='contained'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("buyNow")}
            </Button>
            <Button
              className='landing-pricing-button-outlined'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("free30DaysTrail$0")}
            </Button>
          </Grid>
          <Grid className={`landing-pricing-grid landing-pricing-grid-background ${isMobileOrTablet && 'landing-pricing-grid-width'}`} item xs={12} sm={6} md={3} style={{
            marginBottom: isMobileOrTablet ? "20px" : "0",
            marginLeft: isMobileOrTablet ? "20px" : "0",
          }}>
            <Button className='landing-pricing-pro-out-popular' variant='contained'>
              {t("hide")}
            </Button>
            <Typography className='landing-secondary-color landing-pricing-heading'>
              {t("basicPlus")}
            </Typography>
            <Typography className='landing-secondary-color landing-price-title'>
              <sup className='landing-price-super-script'>$</sup>
              {monthlyOrAnnualPlanCheck ? 384 : 40}
              <sub className='landing-price-sub-script'>
                /{monthlyOrAnnualPlanCheck ? "year" : "mo"}.
              </sub>
            </Typography>
            <Typography className='landing-price-description'>
              {t("singleDisplay/MultipleDisplay")}
            </Typography>
            {Array(5)
              .fill("")
              .map((element: string, index: number) => (
                <Grid key={index} display='flex' mt={2}>
                  <CheckCircleIcon
                    className={`landing-pricing-content-list-icon landing-secondary-color ${
                      basicPlusOrangeListingIndex.includes(index) && "landing-orange-color"
                    } `}
                  />
                  <Typography
                    className={`landing-text-color landing-list-item-align ${
                      basicPlusOrangeListingIndex.includes(index) && "landing-text-orange-color"
                    } `}
                  >
                    {t(`basicPlusList${index + 1}`)}
                  </Typography>
                </Grid>
              ))}
            <Grid display='flex' mt={2}>
              <CheckCircleIcon className='landing-pricing-content-list-icon landing-secondary-color' />
              <Typography className='landing-text-color landing-list-item-align'>
                {t("24/7Support")}
              </Typography>
            </Grid>
            {/* TODO: later - instead of margin top use flex property */}
            <Grid className='landing-pricing-extra-grid' mt='30%'>
              <Grid display='flex' alignContent='space-between'>
                {/* <FormControlLabel
                className='landing-pricing-checkbox'
                control={
                  <Checkbox
                    checked={basicPlusCheck}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBasicPlusChange(e)}
                  />
                }
                label={t("+$10")}
              /> */}
                {/* <AdsAddOn value={basicPlusValue} setValue={setBasicPlusValue} setCheck={setBasicPlusCheck} handleReset={handleBasicPlusReset}/> */}
              </Grid>
              {/* <Typography className='landing-pricing-checkbox'>{t("$10Description")}</Typography> */}
              <Typography className='landing-secondary-color landing-pricing-total' align='center'>
                {t("total") + `$${handleBasicPlusTotal()}.00`}
              </Typography>
            </Grid>
            <Button
              className='landing-pricing-button-variant'
              variant='contained'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("buyNow")}
            </Button>
            <Button
              className='landing-pricing-button-outlined'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("free30DaysTrail$0")}
            </Button>
          </Grid>
          <Grid className={`landing-pro-pricing-grid landing-pricing-grid ${isMobileOrTablet && 'landing-pricing-grid-width'}`} item xs={12} sm={6} md={3} style={{
            marginBottom: isMobileOrTablet ? "20px" : "0",
            marginLeft: isMobileOrTablet ? "20px" : "0",
          }}>
            <Button
              className='landing-pricing-pro-popular popular-pricing-heading'
              variant='contained'
            >
              {t("popular")}
            </Button>
            <Typography className='landing-text-white-color landing-pricing-heading'>
              {t("pro")}
            </Typography>
            <Typography className='landing-text-white-color landing-price-title'>
              <sup className='landing-price-super-script'>$</sup>
              {monthlyOrAnnualPlanCheck ? 480 : 50}
              <sub className='landing-price-sub-script landing-text-white'>
                /{monthlyOrAnnualPlanCheck ? "year" : "mo"}.
              </sub>
            </Typography>
            <Typography className='landing-price-description landing-text-white-color'>
              {t("singleDisplay/MultipleDisplay")}
            </Typography>
            {Array(6)
              .fill("")
              .map((element: string, index: number) => (
                <Grid key={index} display='flex' mt={2}>
                  <CheckCircleIcon
                    className={`landing-pricing-content-list-icon ${
                      popularOrangeListingIndex.includes(index)
                        ? "landing-orange-color"
                        : "landing-text-white-color"
                    } `}
                  />
                  <Typography
                    className={`landing-list-item-align ${
                      popularOrangeListingIndex.includes(index)
                        ? "landing-text-orange-color"
                        : "landing-text-white-color"
                    } `}
                  >
                    {t(`proList${index + 1}`)}
                  </Typography>
                </Grid>
              ))}
            <Grid display='flex' mt={2}>
              <CheckCircleIcon className='landing-pricing-content-list-icon landing-text-white-color' />
              <Typography className='landing-text-white-color landing-list-item-align'>
                {t("24/7Support")}
              </Typography>
            </Grid>
            {/* TODO: later - instead of margin top use flex property */}
            <Grid className='landing-pricing-extra-grid' mt='12%'>
              <Grid display='flex' alignContent='space-between'>
                {/* <FormControlLabel
                className='landing-text-white-color landing-pricing-checkbox'
                control={
                  <Checkbox
                    checked={proCheck}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleProChange(e)}
                    className='landing-text-white-color'
                  />
                }
                label={t("+$10")}
              /> */}
                {/* <AdsAddOn proPack={true} value={proValue} setValue={setProValue} setCheck={setProCheck} handleReset={handleProReset}/> */}
              </Grid>
              {/* <Typography className='landing-text-white-color landing-pricing-checkbox'>
              {t("$10Description")}
            </Typography> */}
              <Typography className='landing-text-white-color landing-pricing-total' align='center'>
                {t("total") + `$${handleProTotal()}.00`}
              </Typography>
            </Grid>
            <Button
              className='landing-pricing-pro-button-variant'
              variant='contained'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("buyNow")}
            </Button>
            <Button
              className='landing-pricing-pro-button-outlined'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("free30DaysTrail$0")}
            </Button>
          </Grid>
          <Grid className={`landing-pricing-grid landing-pricing-grid-background ${isMobileOrTablet && 'landing-pricing-grid-width'}`} item xs={12} sm={6} md={3} style={{
            marginBottom: isMobileOrTablet ? "20px" : "0",
            marginLeft: isMobileOrTablet ? "20px" : "0",
          }}>
            <Button className='landing-pricing-pro-out-popular' variant='contained'>
              {t("hide")}
            </Button>
            <Typography className='landing-secondary-color landing-pricing-heading'>
              {t("premium")}
            </Typography>
            <Typography className='landing-secondary-color landing-price-title'>
              <sup className='landing-price-super-script'>$</sup>
              {monthlyOrAnnualPlanCheck ? 720 : 75}
              <sub className='landing-price-sub-script'>
                /{monthlyOrAnnualPlanCheck ? "year" : "mo"}.
              </sub>
            </Typography>
            <Typography className='landing-price-description'>
              {t("singleDisplay/MultipleDisplay")}
            </Typography>
            {Array(7)
              .fill("")
              .map((element: string, index: number) => (
                <Grid key={index} display='flex' mt={2}>
                  <CheckCircleIcon
                    className={`landing-pricing-content-list-icon ${
                      premiumOrangeListingIndex.includes(index)
                        ? "landing-orange-color"
                        : "landing-secondary-color"
                    } `}
                  />
                  <Typography
                    className={`landing-text-color landing-list-item-align ${
                      premiumOrangeListingIndex.includes(index)
                        ? "landing-text-orange-color"
                        : "landing-text-color"
                    } `}
                  >
                    {t(`premiumList${index + 1}`)}
                  </Typography>
                </Grid>
              ))}
            <Grid display='flex' mt={2}>
              <CheckCircleIcon className='landing-pricing-content-list-icon landing-secondary-color' />
              <Typography className='landing-text-color landing-list-item-align'>
                {t("24/7Support")}
              </Typography>
            </Grid>
            <Grid className='landing-pricing-extra-grid' mt={3}>
              <Typography className='landing-secondary-color landing-pricing-total' align='center'>
                {t("total") + `$${monthlyOrAnnualPlanCheck ? 720 : 75}.00`}
              </Typography>
            </Grid>
            <Button
              className='landing-pricing-button-variant'
              variant='contained'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("buyNow")}
            </Button>
            <Button
              id='about-us'
              className='landing-pricing-button-outlined'
              onClick={() => gotoScroll("contact-us")}
            >
              {t("free30DaysTrail$0")}
            </Button>
          </Grid>
        </Grid>
      </>
    </Grid>
  );
};

export default Pricing;
